import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { IoSearchOutline, IoEyeOffOutline, IoCloseOutline } from 'react-icons/io5';
import './Filters.scss';
import { getDomains } from '../../../api/url_data';
import { useUrlDataContext } from '../lib/UrlDataContext';
import Switch from 'react-switch';
import { ADMIN_SCOPES } from '../../../constants/admin_scope_constants';
import { hasScope } from '../../../helpers/scope_helpers';

const FiltersComponent = ({ onRequestClose, onOpenIgnoredDomains, currentUser }) => {
  const adminScopes = currentUser?.scopes || [];
  const { filters, changeFilters, excludedDomains, excludeDomain, removeExcludedDomain } = useUrlDataContext();
  const [search, setSearch] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const isMounted = useRef(true);
  const inputRef = useRef(null);
  const [domains, setDomains] = useState({ is_partner: [], is_not_partner: [] });

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isMounted.current) {
        setDebouncedValue(search);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    const fetchDomains = async () => {
      try {
        // Exclude domain filter when fetching domains for search, so users can search all domains
        const { domain, ...filtersWithoutDomain } = filters;
        const { data } = await getDomains({ search: debouncedValue, filters: filtersWithoutDomain });

        if (!cancelled) {
          setDomains(data);
        }
      } catch {
        if (!cancelled) {
          window.ALERT.error('Failed to fetch domains. Please try again.');
        }
      }
    };

    fetchDomains();

    return () => {
      cancelled = true;
    };
  }, [debouncedValue, filters]);

  const onDomainChange = filterDomain => {
    // The include filter and an exclusion for the same domain would return zero rows
    const remainingExcluded = excludedDomains.filter(domain => domain !== filterDomain);
    changeFilters({ ...filters, domain: filterDomain, excludedDomains: remainingExcluded.length ? remainingExcluded : null });
    setSearch(filterDomain);
    onRequestClose?.();
  };

  const toggleShowEscalated = () => {
    changeFilters({ ...filters, showEscalated: filters.showEscalated ? null : true });
  };

  return (
    <div className='url-data-filters'>
      <div className='search'>
        <IoSearchOutline size={20} />
        <input type='text' placeholder='Search domains' value={search} onChange={e => setSearch(e.target.value)} ref={inputRef} />
      </div>

      {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
        <button className='manage-domains-button' onClick={onOpenIgnoredDomains}>
          Manage Domains
        </button>
      )}

      <div className='show-escalated-switch'>
        <Switch
          onChange={toggleShowEscalated}
          checked={filters.showEscalated}
          height={14}
          width={24}
          onColor='#333'
          offColor='#ccc'
          className='switch'
          checkedIcon={false}
          uncheckedIcon={false}
        />
        <label onClick={toggleShowEscalated}>Show Escalated</label>
      </div>

      {excludedDomains.length > 0 && (
        <div className='excluded-domains'>
          <p className='domain-header'>Hidden Domains</p>

          <ul className='excluded-domain-chips'>
            {excludedDomains.map(domain => (
              <li key={domain}>
                <button title={`Show ${domain} in the queue again`} onClick={() => removeExcludedDomain(domain)}>
                  <span>{domain}</span>
                  <IoCloseOutline size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className='domains'>
        <li>
          <p className='domain-header'>ShopMy Partners</p>

          <ul>
            {domains.is_partner.map(domain => (
              <li key={domain.domain} className='domain-item'>
                <button className='select-domain' onClick={() => onDomainChange(domain.domain)}>
                  <span className='domain'>{domain.domain}</span>
                  <span className='count'>{domain.count}</span>
                </button>
                <button className='exclude-domain' title={`Hide ${domain.domain} from the queue`} onClick={() => excludeDomain(domain.domain)}>
                  <IoEyeOffOutline size={16} />
                </button>
              </li>
            ))}
          </ul>
        </li>

        <li>
          <p className='domain-header'>Other Brands</p>

          <ul>
            {domains.is_not_partner.map(domain => (
              <li key={domain.domain} className='domain-item'>
                <button className='select-domain' onClick={() => onDomainChange(domain.domain)}>
                  <span className='domain'>{domain.domain}</span>
                  <span className='count'>{domain.count}</span>
                </button>
                <button className='exclude-domain' title={`Hide ${domain.domain} from the queue`} onClick={() => excludeDomain(domain.domain)}>
                  <IoEyeOffOutline size={16} />
                </button>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </div>
  );
};

const mapStateToProps = state => {
  const { users } = state;
  return { currentUser: users.currentUser };
};

export const Filters = connect(mapStateToProps)(FiltersComponent);
